import React, { useEffect, useRef, useState } from "react";
import "./SchoolV2.css";
import engineeringStudents from "../assets/img/indian-engineering-students.webp";
import learningExperience from "../assets/img/indian-learning-experience.webp";

const programmeGroups = [
  {
    label: "Undergraduate",
    programmes: [
      ["B.Tech", "Computer Science & Engineering", "4 Years"],
      ["B.Tech", "Mechanical Engineering", "4 Years"],
      ["B.Tech", "Civil Engineering", "4 Years"],
      ["B.Tech", "Electrical & Electronics", "4 Years"],
    ],
  },
  {
    label: "Postgraduate",
    programmes: [
      ["M.Tech", "Computer Science & Engineering", "2 Years"],
      ["M.Tech", "Civil Engineering", "2 Years"],
      ["M.Tech", "Mechanical Engineering", "2 Years"],
    ],
  },
  {
    label: "Diploma",
    programmes: [
      ["Diploma", "Computer Science Engineering", "3 Years"],
      ["Diploma", "Mechanical Engineering", "3 Years"],
      ["Diploma", "Civil Engineering", "3 Years"],
    ],
  },
  {
    label: "Doctoral",
    programmes: [
      ["Ph.D.", "Engineering & Technology", "As per norms"],
      ["Ph.D.", "Computer Science", "As per norms"],
    ],
  },
];

const learningJourney = [
  {
    number: "01",
    title: "Strong foundations",
    text: "Build conceptual clarity through active classrooms, demonstrations and guided problem-solving.",
  },
  {
    number: "02",
    title: "Practical exploration",
    text: "Translate theory into practice through laboratories, workshops, simulation and field exposure.",
  },
  {
    number: "03",
    title: "Industry application",
    text: "Work on live briefs, internships, expert-led assignments and multidisciplinary challenges.",
  },
  {
    number: "04",
    title: "Innovation & leadership",
    text: "Develop original solutions while strengthening communication, teamwork and entrepreneurial thinking.",
  },
];

const facilities = [
  {
    title: "Advanced Computing Centre",
    tag: "Technology",
    image:
      "https://images.unsplash.com/photo-1531297484001-80022131f5a1?auto=format&fit=crop&w=1200&q=88",
  },
  {
    title: "Engineering Workshop",
    tag: "Making",
    image:
      "https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&w=1200&q=88",
  },
  {
    title: "Innovation Studio",
    tag: "Collaboration",
    image:
      "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=88",
  },
  {
    title: "Research Laboratories",
    tag: "Discovery",
    image:
      "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?auto=format&fit=crop&w=1200&q=88",
  },
];

const faqs = [
  {
    question: "What engineering programmes are offered?",
    answer:
      "The School offers undergraduate, postgraduate, diploma and doctoral pathways across major engineering disciplines. Replace the demonstration list with the University’s approved current offerings.",
  },
  {
    question: "How much practical learning is included?",
    answer:
      "Practical learning is integrated through laboratory sessions, workshops, software simulations, field visits, mini-projects and capstone work.",
  },
  {
    question: "Does the School provide internship opportunities?",
    answer:
      "Students are guided towards internships, live projects and industry interaction based on programme requirements and available partnerships.",
  },
  {
    question: "What career support is available?",
    answer:
      "Career preparation includes aptitude development, technical training, résumé building, mock interviews, soft-skills training and recruitment support.",
  },
];

function CountUp({ end, suffix = "" }) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const target = ref.current;
    if (!target) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        const startedAt = performance.now();
        const duration = 1300;

        const tick = (now) => {
          const progress = Math.min((now - startedAt) / duration, 1);
          setValue(Math.round(end * (1 - Math.pow(1 - progress, 3))));
          if (progress < 1) requestAnimationFrame(tick);
        };

        requestAnimationFrame(tick);
        observer.disconnect();
      },
      { threshold: 0.4 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [end]);

  return (
    <strong ref={ref}>
      {value}
      {suffix}
    </strong>
  );
}

function TitleBlock({ kicker, title, accent, text, align = "left" }) {
  return (
    <div className={`sv2-title sv2-title--${align}`}>
      <span>{kicker}</span>
      <h2>
        {title} <em>{accent}</em>
      </h2>
      {text && <p>{text}</p>}
    </div>
  );
}

export default function SchoolV2() {
  const [programmeTab, setProgrammeTab] = useState(0);
  const [activeFaq, setActiveFaq] = useState(0);

  useEffect(() => {
    const targets = document.querySelectorAll(".school-v2 .sv2-reveal");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("sv2-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px" }
    );

    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, []);

  return (
    <main className="school-v2">
      <section className="sv2-hero" id="sv2-top">
        <div className="sv2-hero__rail" aria-hidden="true">
          <span>ISBM UNIVERSITY</span>
          <i />
          <span>ENGINEERING & TECHNOLOGY</span>
        </div>

        <div className="sv2-shell sv2-hero__layout">
          <div className="sv2-hero__copy">
            <nav className="sv2-breadcrumb" aria-label="Breadcrumb">
              <a href="/">Home</a><span>→</span><a href="/academics">Schools</a>
              <span>→</span><b>Engineering</b>
            </nav>

            <span className="sv2-overline">School of Engineering & Technology</span>
            <h1>
              Imagine.
              <br />
              Engineer.
              <br />
              <em>Transform.</em>
            </h1>
            <p>
              An interdisciplinary school preparing future-ready Indian
              engineers to solve regional, national and global challenges.
            </p>
            <div className="sv2-hero__actions">
              <a className="sv2-btn sv2-btn--coral" href="/apply-online">Apply now <span>↗</span></a>
              <a className="sv2-link" href="#sv2-programmes">View programmes <span>↓</span></a>
            </div>
          </div>

          <div className="sv2-hero__visual">
            <div className="sv2-hero__shape" />
            <img
              src={engineeringStudents}
              alt="Indian engineering students developing a robotics project"
            />
            <div className="sv2-hero__badge">
              <span>Learning by doing</span>
              <b>30+</b>
              <small>Labs & practical spaces</small>
            </div>
            <div className="sv2-hero__caption">
              <b>Build ideas in the real world.</b>
              <span>Projects · Research · Innovation</span>
            </div>
          </div>
        </div>

        <a className="sv2-scroll" href="#sv2-intro" aria-label="Scroll to introduction">
          <span>Scroll to explore</span><i>↓</i>
        </a>
      </section>

      <section className="sv2-intro" id="sv2-intro">
        <div className="sv2-shell">
          <div className="sv2-intro__statement sv2-reveal">
            <span>Our purpose</span>
            <p>
              We prepare engineers who connect <em>technical intelligence</em>{" "}
              with Indian values, social responsibility and the confidence to
              contribute to a self-reliant nation.
            </p>
          </div>

          <div className="sv2-intro__facts sv2-reveal">
            <article><CountUp end={12} suffix="+" /><span>Career-focused programmes</span></article>
            <article><CountUp end={30} suffix="+" /><span>Laboratories and workshops</span></article>
            <article><CountUp end={250} suffix="+" /><span>Recruiting organisations*</span></article>
            <article><CountUp end={95} suffix="%" /><span>Placement assistance*</span></article>
          </div>
          <small className="sv2-disclaimer">*Replace demonstration statistics with verified current data.</small>
        </div>
      </section>

      <nav className="sv2-section-nav" aria-label="Page sections">
        <div className="sv2-shell">
          {[
            ["About", "sv2-about"],
            ["Programmes", "sv2-programmes"],
            ["Experience", "sv2-experience"],
            ["Facilities", "sv2-facilities"],
            ["Research", "sv2-research"],
            ["Careers", "sv2-careers"],
            ["FAQ", "sv2-faq"],
          ].map(([label, id]) => <a href={`#${id}`} key={id}>{label}</a>)}
        </div>
      </nav>

      <section className="sv2-about sv2-section" id="sv2-about">
        <div className="sv2-shell sv2-about__layout">
          <div className="sv2-about__copy sv2-reveal">
            <TitleBlock
              kicker="Built for the future"
              title="Rooted in India."
              accent="Ready for the world."
              text="Our academic environment combines strong engineering fundamentals, emerging technologies, Indian Knowledge Systems and meaningful practice. Students learn to solve real challenges relevant to industry, communities and national development."
            />

            <div className="sv2-about__values">
              <article><b>01</b><h3>Academic rigour</h3><p>Clear concepts, analytical thinking and disciplined engineering practice.</p></article>
              <article><b>02</b><h3>Practical confidence</h3><p>Laboratories, projects and application-led learning from the beginning.</p></article>
              <article><b>03</b><h3>Responsible innovation</h3><p>Solutions shaped by sustainability, ethics, Indian realities and human needs.</p></article>
            </div>
          </div>

          <div className="sv2-about__gallery sv2-reveal">
            <img
              className="sv2-about__gallery-main"
              src={learningExperience}
              alt="Indian engineering students learning with a faculty mentor"
              loading="lazy"
            />
            <img
              className="sv2-about__gallery-small"
              src="https://images.unsplash.com/photo-1581092334651-ddf26d9a09d0?auto=format&fit=crop&w=700&q=90"
              alt="Practical engineering learning"
              loading="lazy"
            />
            <div><span>Think</span><span>Test</span><span>Create</span></div>
          </div>
        </div>
      </section>

      <section className="sv2-programmes sv2-section" id="sv2-programmes">
        <div className="sv2-shell">
          <div className="sv2-programmes__top sv2-reveal">
            <TitleBlock
              kicker="Find your direction"
              title="Programmes designed around"
              accent="where you want to go."
              text="Choose a qualification level to explore academic pathways offered through the School."
            />
            <a className="sv2-round-link" href="/programmes" aria-label="View all programmes">↗</a>
          </div>

          <div className="sv2-programmes__explorer sv2-reveal">
            <div className="sv2-programmes__tabs" role="tablist">
              {programmeGroups.map((group, index) => (
                <button
                  type="button"
                  key={group.label}
                  role="tab"
                  aria-selected={programmeTab === index}
                  className={programmeTab === index ? "active" : ""}
                  onClick={() => setProgrammeTab(index)}
                >
                  <span>0{index + 1}</span>
                  <b>{group.label}</b>
                  <i>→</i>
                </button>
              ))}
            </div>

            <div className="sv2-programmes__panel">
              <span>{programmeGroups[programmeTab].label} programmes</span>
              {programmeGroups[programmeTab].programmes.map(([degree, title, duration], index) => (
                <article key={`${degree}-${title}`} className="sv2-programme-card">
                  <small>{String(index + 1).padStart(2, "0")}</small>
                  <div><b>{degree}</b><h3>{title}</h3></div>
                  <span>{duration}</span>
                  <a href="/programme-details" aria-label={`View ${title}`}>↗</a>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="sv2-experience sv2-section" id="sv2-experience">
        <div className="sv2-shell">
          <TitleBlock
            kicker="The learning experience"
              title="From classroom concepts"
              accent="to nation-building ideas."
              text="A connected learning model that moves from conceptual understanding to practical applications relevant to Indian industry and society."
            align="center"
          />

          <div className="sv2-experience__layout">
            <div className="sv2-experience__image sv2-reveal">
              <img
                src={learningExperience}
                alt="Indian university students learning collaboratively"
                loading="lazy"
              />
              <div><b>Experience-led learning</b><span>Designed for active minds</span></div>
            </div>

            <div className="sv2-experience__timeline">
              {learningJourney.map((item) => (
                <article className="sv2-reveal" key={item.number}>
                  <span>{item.number}</span>
                  <div><h3>{item.title}</h3><p>{item.text}</p></div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="sv2-facilities sv2-section" id="sv2-facilities">
        <div className="sv2-shell">
          <div className="sv2-facilities__heading sv2-reveal">
            <TitleBlock
              kicker="Spaces to explore"
              title="A campus that becomes"
              accent="your learning laboratory."
              text="Modern spaces support practical investigation, technical experimentation, teamwork and creative problem-solving."
            />
            <a className="sv2-link" href="/facilities">Explore all facilities <span>↗</span></a>
          </div>

          <div className="sv2-facilities__bento">
            {facilities.map((facility, index) => (
              <article className={`sv2-facility sv2-facility--${index + 1} sv2-reveal`} key={facility.title}>
                <img src={facility.image} alt={facility.title} loading="lazy" />
                <div className="sv2-facility__wash" />
                <span>{facility.tag}</span>
                <div><h3>{facility.title}</h3><a href="/facilities">Discover space ↗</a></div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="sv2-research sv2-section" id="sv2-research">
        <div className="sv2-shell sv2-research__layout">
          <div className="sv2-research__visual sv2-reveal">
            <div className="sv2-research__circle">
              <img
                src="https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1100&q=90"
                alt="University research laboratory"
                loading="lazy"
              />
            </div>
            <span>RESEARCH<br />WITH<br />PURPOSE</span>
          </div>

          <div className="sv2-research__copy sv2-reveal">
            <TitleBlock
              kicker="Research & innovation"
              title="Questions that lead to"
              accent="meaningful change."
              text="Our research culture encourages faculty and students to address India’s technological, environmental and social priorities through publications, patents, prototypes and practical solutions."
            />
            <div className="sv2-research__numbers">
              <article><CountUp end={120} suffix="+" /><span>Research publications*</span></article>
              <article><CountUp end={18} suffix="+" /><span>Patents & innovations*</span></article>
              <article><CountUp end={35} suffix="+" /><span>Active projects*</span></article>
            </div>
            <div className="sv2-research__tags">
              <span>Artificial Intelligence</span><span>Smart Cities & Infrastructure</span>
              <span>Renewable Energy</span><span>Make in India & Advanced Manufacturing</span>
            </div>
            <a className="sv2-btn sv2-btn--ink" href="/research">Explore research <span>↗</span></a>
          </div>
        </div>
      </section>

      <section className="sv2-mentor sv2-section">
        <div className="sv2-shell sv2-mentor__card sv2-reveal">
          <img
            src="https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=900&q=90"
            alt="Dean of Engineering"
            loading="lazy"
          />
          <div>
            <span>Message from the Dean</span>
            <blockquote>
              “We encourage our students to combine technical excellence with
              integrity, social responsibility and the courage to build a
              stronger, more sustainable and self-reliant India.”
            </blockquote>
            <b>Prof. (Dr.) Your Dean Name</b>
            <small>Dean · School of Engineering & Technology</small>
          </div>
          <i>“</i>
        </div>
      </section>

      <section className="sv2-careers" id="sv2-careers">
        <div className="sv2-careers__image">
          <img
            src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=1800&q=90"
            alt="Students preparing for professional careers"
            loading="lazy"
          />
        </div>
        <div className="sv2-careers__content sv2-reveal">
          <span>Career preparation</span>
          <h2>Step into the professional world with <em>confidence.</em></h2>
          <p>
            Develop technical, communication and workplace skills through
            structured training, internships, mentoring and recruiter engagement.
          </p>
          <ul>
            <li><b>01</b>Technical and aptitude development</li>
            <li><b>02</b>Internships and live assignments</li>
            <li><b>03</b>Résumé and interview preparation</li>
            <li><b>04</b>Campus recruitment assistance</li>
          </ul>
          <a className="sv2-btn sv2-btn--light" href="/placements">Visit placements <span>↗</span></a>
        </div>
      </section>

      <section className="sv2-faq sv2-section" id="sv2-faq">
        <div className="sv2-shell sv2-faq__layout">
          <div className="sv2-faq__intro sv2-reveal">
            <TitleBlock
              kicker="Frequently asked"
              title="Answers for your"
              accent="next step."
              text="Helpful information for prospective students and families."
            />
            <div className="sv2-faq__contact">
              <span>Need personal guidance?</span>
              <a href="/contact">Speak with admissions ↗</a>
            </div>
          </div>

          <div className="sv2-faq__items sv2-reveal">
            {faqs.map((item, index) => {
              const open = activeFaq === index;
              return (
                <article className={open ? "open" : ""} key={item.question}>
                  <button type="button" onClick={() => setActiveFaq(open ? -1 : index)} aria-expanded={open}>
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <b>{item.question}</b><i>{open ? "−" : "+"}</i>
                  </button>
                  <div><p>{item.answer}</p></div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="sv2-cta">
        <div className="sv2-cta__pattern" aria-hidden="true">ENGINEER THE FUTURE</div>
        <div className="sv2-shell sv2-cta__content sv2-reveal">
          <span>Admissions open</span>
          <h2>Your ideas belong <em>here.</em></h2>
          <p>Begin your engineering journey at ISBM University.</p>
          <div>
            <a className="sv2-btn sv2-btn--coral" href="/apply-online">Apply online <span>↗</span></a>
            <a className="sv2-link sv2-link--white" href="/download-brochure">Download brochure <span>↓</span></a>
          </div>
        </div>
      </section>
    </main>
  );
}
